import * as React from 'react'
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LogoutIcon from '@mui/icons-material/Logout'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import routes from '../../routes'
import RushmyFoodLogo from '../../assets/img/rushmyfood-logo.png'
import { useTranslation, withTranslation } from 'react-i18next'
import { useLocation, useHistory } from 'react-router-dom'
import { gql, useApolloClient, useQuery } from '@apollo/client'
import { getStripeRestaurantDashboard, getSidebarRestaurantFlags } from '../../apollo/queries'
import { jakartaFontTheme } from '../../utils/plusJakartaTheme'
import SignOutAllDevicesDialog from '../Security/SignOutAllDevicesDialog'

const STRIPE_DASHBOARD = gql`${getStripeRestaurantDashboard}`
const RESTAURANT_FLAGS = gql`${getSidebarRestaurantFlags}`

export const drawerWidth = 240
export const collapsedWidth = 72
const BRAND = '#ED1D26'

/* The restaurant panel's links, grouped. Keyed by route path so a new route
   or a reorder in routes.js cannot silently shift a heading onto the wrong
   item (the old version keyed headings by array index). Anything not listed
   still shows, under MORE, rather than disappearing. */
const GROUPS = [
  { label: 'Overview', paths: ['/dashboard', '/orders', '/partner-orders', '/ratings'] },
  { label: 'Menu & store', paths: ['/profile', '/advanced-menus', '/timings', '/truck-stops', '/catering', '/deliverybounds', '/delivery-zones', '/promotions'] },
  { label: 'Drivers', paths: ['/dispatch/:id', '/driver-payouts', '/driver-override', '/driver-payout-requests'] },
  { label: 'Money', paths: ['/stripe'] },
  { label: 'Team', paths: ['/staff', '/support-tickets'] }
]

function groupRoutes(list) {
  const used = new Set()
  const out = GROUPS.map(g => {
    const items = g.paths.map(p => list.find(r => r.path === p)).filter(Boolean)
    items.forEach(r => used.add(r))
    return { label: g.label, items }
  })
  const rest = list.filter(r => !used.has(r))
  if (rest.length) out.push({ label: 'More', items: rest })
  return out.filter(g => g.items.length)
}

function Sidebar(props) {
  const { collapsed = false, onToggleCollapsed } = props
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const client = useApolloClient()
  const { window } = props
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [signOutAll, setSignOutAll] = React.useState(false)
  const [userInfo, setUserInfo] = React.useState({ name: 'User', email: '' })

  const restaurantId = localStorage.getItem('restaurantId')
  const restaurantImage = localStorage.getItem('restaurantImage')
  const restaurantName = localStorage.getItem('restaurantName')

  React.useEffect(() => {
    const load = () => {
      const data = localStorage.getItem('user-enatega')
      if (data) {
        const p = JSON.parse(data)
        setUserInfo({ name: p.name || (p.email ? p.email.split('@')[0] : 'User'), email: p.email || '', userType: p.userType || '' })
      }
    }
    load()
  }, [])

  const handleLogout = () => {
    setAnchorEl(null)
    localStorage.removeItem('user-enatega')
    localStorage.removeItem('restaurantId')
    localStorage.removeItem('restaurantName')
    localStorage.removeItem('restaurantImage')
    client.clearStore()
    history.push('/auth/login')
  }

  const handleProfile = () => {
    setAnchorEl(null)
    history.push('/admin/profile')
  }

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen)
  const container = window !== undefined ? () => window().document.body : undefined

  const userType = (() => {
    try { return JSON.parse(localStorage.getItem('user-enatega'))?.userType } catch { return null }
  })()

  // The Stripe link shows only while both switches (global + this restaurant)
  // are on. Any error — no restaurant picked, not the owner — just hides it.
  const { data: stripeData } = useQuery(STRIPE_DASHBOARD, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  })
  const stripeOn = !!stripeData?.stripeRestaurantDashboard?.enabled

  // Delivery Zones only for self delivery, Truck Stops only for food trucks.
  // Profile's refetch after a switch lands in the same cached restaurant, so
  // the links follow the switches without a reload.
  const { data: flagsData } = useQuery(RESTAURANT_FLAGS, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  })
  const selfDeliveryOn = flagsData?.restaurant?.selfDelivery === true
  const foodTruck = flagsData?.restaurant?.isMobile === true

  // Collect vendor routes
  const vendorRoutes = routes.reduce((acc, prop, key) => {
    const isBackToAdmin = prop.name === t('BackToAdmin') || prop.name === 'Back to Admin'
    if (prop.stripeDashboard && !stripeOn) return acc
    if (prop.selfDeliveryOnly && !selfDeliveryOn) return acc
    if (prop.foodTruckOnly && !foodTruck) return acc
    if (prop.appearInSidebar && !prop.admin && !isBackToAdmin) {
      acc.push({ ...prop, originalKey: key })
    }
    return acc
  }, [])

  // Back to Admin link (only for ADMIN userType)
  const backToAdminRoute = routes.find(r => r.name === 'Back to Admin' || r.name === t('BackToAdmin'))

  const isActive = prop => {
    const base = `${prop.layout}${prop.path.split('/:')[0]}`
    return location.pathname === base || location.pathname.startsWith(base + '/')
  }

  const IconEl = ({ icon: Icon, active }) => {
    if (!Icon) return null
    return (
      <Box className="rmf-nav-icon" sx={{
        width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        '& svg': {
          width: 20, height: 20,
          transition: 'filter 150ms ease',
          filter: active
            ? 'brightness(0) invert(1)'
            : 'brightness(0) invert(1) opacity(0.55)'
        }
      }}>
        <Icon />
      </Box>
    )
  }

  const renderContent = mini => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      bgcolor: '#0f172a', overflowY: 'auto', overflowX: 'hidden',
      '&::-webkit-scrollbar': { width: 0 }
    }}>
      {/* Logo + collapse toggle — the toggle sits on the logo row; only the
          narrow collapsed rail stacks it underneath. */}
      <Box sx={{ px: mini ? 1.5 : 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: mini ? 'column' : 'row', alignItems: 'center', gap: mini ? 1 : 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            bgcolor: '#fff', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)'
          }}>
            <img src={RushmyFoodLogo} alt="RushMyFood" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </Box>
          {!mini && (
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff', lineHeight: 1.2, flex: 1, minWidth: 0 }}>
              RushMyFood
            </Typography>
          )}
          {onToggleCollapsed && (
            <Tooltip title={mini ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton
                size="small"
                onClick={onToggleCollapsed}
                aria-label={mini ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!mini}
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0, color: 'rgba(255,255,255,0.55)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }, '&.Mui-focusVisible': { outline: `2px solid ${BRAND}` } }}>
                {mini ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {/* The restaurant, once — its photo and name. */}
        {restaurantName && !mini && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, mt: 2, p: 1,
            borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)'
          }}>
            {restaurantImage
              ? <img src={restaurantImage} alt="" style={{ width: 28, height: 28, borderRadius: '7px', objectFit: 'cover', flexShrink: 0 }} />
              : (
                <Box aria-hidden sx={{
                  width: 28, height: 28, borderRadius: '7px', flexShrink: 0, bgcolor: 'rgba(237,29,38,0.18)', color: '#FDA29B',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700
                }}>
                  {restaurantName.trim().charAt(0).toUpperCase()}
                </Box>
              )}
            <Typography noWrap sx={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500, minWidth: 0 }}>{restaurantName}</Typography>
          </Box>
        )}
      </Box>

      {/* Nav links, in sections */}
      <Box component="nav" aria-label="Restaurant" sx={{ px: mini ? 1 : 1.5, py: 1.5, flex: 1 }}>
        {groupRoutes(vendorRoutes).map((group, gi) => (
          <Box key={group.label} role="group" aria-label={group.label}>
            {mini ? (
              gi > 0 ? <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.08)', mx: 1, my: 1.25 }} /> : null
            ) : (
              <Typography sx={{
                fontSize: '0.6875rem', fontWeight: 600,
                color: 'rgba(255,255,255,0.5)',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                px: 1.5, mt: gi === 0 ? 0.5 : 3, mb: 1
              }}>
                {group.label}
              </Typography>
            )}
            {group.items.map(prop => {
              const active = isActive(prop)
              const link = (
                <Box
                  key={prop.originalKey}
                  component="a"
                  href={'#' + prop.layout + prop.path.replace(':id', restaurantId)}
                  aria-current={active ? 'page' : undefined}
                  aria-label={mini ? prop.name : undefined}
                  sx={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    justifyContent: mini ? 'center' : 'flex-start',
                    px: mini ? 0 : 1.5, py: 0.9, mb: 0.25,
                    borderRadius: '10px', textDecoration: 'none', cursor: 'pointer',
                    bgcolor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                    transition: 'background-color 150ms ease',
                    // Brand bar on the active item, drawn over the edge so the label never shifts.
                    '&::before': {
                      content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
                      borderRadius: '0 3px 3px 0', bgcolor: BRAND,
                      transform: active ? 'scaleY(1)' : 'scaleY(0)', transition: 'transform 180ms ease'
                    },
                    '@media (prefers-reduced-motion: reduce)': { '&::before': { transition: 'none' } },
                    '&:hover': {
                      bgcolor: active ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)',
                      '& .rmf-nav-label': { color: '#fff' },
                      '& .rmf-nav-icon svg': { filter: 'brightness(0) invert(1) opacity(0.85)' }
                    },
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 1 }
                  }}
                >
                  <IconEl icon={prop.icon} active={active} />
                  {!mini && (
                    <Typography noWrap className="rmf-nav-label" sx={{
                      fontSize: '0.83rem',
                      fontWeight: active ? 600 : 400,
                      color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                      lineHeight: 1,
                      // noWrap clips to the line box; Plus Jakarta's descenders
                      // (g, p, y) hang below it. Pad the clip box, net height unchanged.
                      py: '0.2em',
                      my: '-0.2em',
                      transition: 'color 150ms ease'
                    }}>
                      {prop.name}
                    </Typography>
                  )}
                </Box>
              )
              return mini
                ? <Tooltip key={prop.originalKey} title={prop.name} placement="right">{link}</Tooltip>
                : link
            })}
          </Box>
        ))}

        {/* Back to Admin link */}
        {userType === 'ADMIN' && backToAdminRoute && (
          <Box
            component="a"
            href={'#' + backToAdminRoute.layout + backToAdminRoute.path}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 1.5, py: 0.9, mt: 1,
              borderRadius: '10px', textDecoration: 'none', cursor: 'pointer',
              borderLeft: '3px solid transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }
            }}
          >
            <IconEl icon={backToAdminRoute.icon} active={false} />
            {!mini && (
              <Typography sx={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1 }}>
                Back to Admin
              </Typography>
            )}
          </Box>
        )}

        {/* Back to Merchant Panel — shown for vendor userType */}
        {userType === 'VENDOR' && (
          <Box
            onClick={() => history.push('/restaurant/list')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 1.5, py: 0.9, mt: 1,
              borderRadius: '10px', cursor: 'pointer',
              borderLeft: '3px solid transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }
            }}
          >
            <Box sx={{ width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowBackIcon sx={{ width: 20, height: 20, color: 'rgba(255,255,255,0.55)' }} />
            </Box>
            {!mini && (
              <Typography sx={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1 }}>
                Merchant Panel
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* User profile at bottom */}
      <Box sx={{ px: mini ? 1 : 1.5, pb: 2, flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.07)', pt: 1.5 }}>
        <Box
          onClick={e => setAnchorEl(e.currentTarget)}
          role="button"
          tabIndex={0}
          aria-label={`Account menu for ${userInfo.name}`}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAnchorEl(e.currentTarget) } }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            justifyContent: mini ? 'center' : 'flex-start',
            px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }
          }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#4ECDC4', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>
            {userInfo.name.charAt(0).toUpperCase()}
          </Avatar>
          {!mini && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userInfo.name}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userInfo.email}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18, flexShrink: 0 }} />
            </>
          )}
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 160, mb: 1 } }}>
          <MenuItem onClick={handleProfile} sx={{ py: 1.2, fontSize: '0.85rem' }}>
            <ListItemIcon><PersonOutlineIcon sx={{ color: '#666' }} /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); setSignOutAll(true) }} sx={{ py: 1.2, fontSize: '0.85rem' }}>
            <ListItemIcon><DevicesRoundedIcon sx={{ color: '#666' }} /></ListItemIcon>
            Sign out of all devices
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleLogout} sx={{ py: 1.2, fontSize: '0.85rem', color: '#ef4444' }}>
            <ListItemIcon><LogoutIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
            Log Out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )

  return (
    // Font family only: the nav uses the same typeface as the Profile page.
    <ThemeProvider theme={jakartaFontTheme}>
      <AppBar position="fixed" sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        display: { sm: 'none' },
        bgcolor: '#0f172a'
      }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start"
            onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <SignOutAllDevicesDialog open={signOutAll} onClose={() => setSignOutAll(false)} onSignedOut={handleLogout} />

      <Box sx={{ width: { sm: collapsed ? collapsedWidth : drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer container={container} variant="temporary" open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '75%', bgcolor: '#0f172a', border: 'none' }
          }}>
          {renderContent(false)}
        </Drawer>
        <Drawer variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? collapsedWidth : drawerWidth,
              transition: 'width 180ms ease',
              overflowX: 'hidden',
              bgcolor: '#0f172a',
              border: 'none',
              borderRight: '1px solid rgba(255,255,255,0.06)'
            }
          }}
          open>
          {renderContent(collapsed)}
        </Drawer>
      </Box>
    </ThemeProvider>
  )
}

export default withTranslation()(Sidebar)